// 从旧灵智原生（Obsidian 仓库）迁移来的资料里有 [[双链]] 和 ![[附件]]。迁移时为每篇资料生成了一份链接清单
// （custom_metadata.lingzhi_link_manifest，指向 local://<tenant>/lingzhi-maps/<sha256>.json），把链接目标映射到：
//   - 附件：local://<tenant>/lingzhi-assets/<sha256>.<ext>（受保护文件，要带登录令牌去取）
//   - 资料：/platform/knowledge-bases/<kb>?knowledge_id=<id>
// 逻辑沿用 lingzhi-server/frontend/src/utils/lingzhiLinks.ts（已上线），这里改成给 react-markdown 用：
// 先把正文里的 [[…]] 变成链接 / 图片节点，渲染时再按清单换成真实地址。原文字节不改。

export type LingzhiLink = { kind: 'asset' | 'knowledge'; url: string }
export type LingzhiLinkManifest = Record<string, LingzhiLink>

export const MANIFEST_PATH = /^local:\/\/\d+\/lingzhi-maps\/[a-f0-9]{64}\.json$/
const ASSET = /^local:\/\/\d+\/lingzhi-assets\/[a-f0-9]{64}\.(png|jpg|jpeg|gif|webp|svg|pdf|mp3|wav|mp4|docx|xlsx|pptx)$/
const KNOWLEDGE = /^\/platform\/knowledge-bases\/([a-f0-9-]{36})\?knowledge_id=([a-f0-9-]{36})$/
const IMAGE = /\.(png|jpg|jpeg|gif|webp|svg)$/i

/** Obsidian 的别名、标题锚点、编码空格都去掉，只留目标。 */
export function normalizeLingzhiTarget(value: string): string {
  try {
    value = decodeURIComponent(value)
  } catch {
    // 保留无法解码的原文
  }
  return value.trim().replace(/^<|>$/g, '').split('|')[0].split('#')[0].trim()
}

export function validLingzhiLink(value: unknown): value is LingzhiLink {
  if (!value || typeof value !== 'object') return false
  const item = value as LingzhiLink
  return (item.kind === 'asset' && ASSET.test(item.url)) || (item.kind === 'knowledge' && KNOWLEDGE.test(item.url))
}

export function parseManifest(raw: unknown): LingzhiLinkManifest {
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw)
    } catch {
      return {}
    }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: LingzhiLinkManifest = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) if (validLingzhiLink(value)) out[key] = value
  return out
}

export function findLingzhiLink(manifest: LingzhiLinkManifest, target: string): LingzhiLink | null {
  const key = normalizeLingzhiTarget(target)
  return Object.prototype.hasOwnProperty.call(manifest, key) ? manifest[key] : null
}

export const isImageAsset = (link: LingzhiLink) => link.kind === 'asset' && IMAGE.test(link.url)

/** 资料链接 → 新前端里的地址（打开知识库并预览这一篇）。 */
export function knowledgeRoute(link: LingzhiLink): string | null {
  const m = link.kind === 'knowledge' ? link.url.match(KNOWLEDGE) : null
  return m ? `/kb/${m[1]}?doc=${m[2]}` : null
}

// ———— remark 插件：把文本里的 [[目标|显示名]] / ![[附件]] 变成链接 / 图片节点 ————
// 节点的 url 用 lingzhi-link: 前缀，渲染时由组件按清单解析；清单里没有的保留原文字。

interface MdNode {
  type: string
  value?: string
  url?: string
  alt?: string
  children?: MdNode[]
}

export const LINGZHI_LINK_PREFIX = 'lingzhi-link:'
const WIKI = /(!?)\[\[([^\]\n]+)\]\]/g
const SKIP = new Set(['code', 'inlineCode', 'link', 'linkReference', 'html'])

export function remarkLingzhiWikiLinks() {
  const visit = (node: MdNode) => {
    if (!node.children) return
    const next: MdNode[] = []
    for (const child of node.children) {
      if (child.type === 'text' && child.value?.includes('[[')) {
        let last = 0
        for (const m of child.value.matchAll(WIKI)) {
          const at = m.index ?? 0
          if (at > last) next.push({ type: 'text', value: child.value.slice(last, at) })
          const [whole, bang, inner] = m
          const label = inner.split('|')[1]?.trim() || normalizeLingzhiTarget(inner)
          const url = LINGZHI_LINK_PREFIX + encodeURIComponent(inner)
          next.push(bang ? { type: 'image', url, alt: label } : { type: 'link', url, children: [{ type: 'text', value: label }] })
          last = at + whole.length
        }
        if (last < child.value.length) next.push({ type: 'text', value: child.value.slice(last) })
        continue
      }
      if (!SKIP.has(child.type)) visit(child)
      next.push(child)
    }
    node.children = next
  }
  return (tree: MdNode) => visit(tree)
}

/** 渲染时：lingzhi-link: 地址 → 清单里的目标（也接受普通的相对路径链接，比如迁移前的 ![](附件.png)）。 */
export function resolveMarkdownTarget(manifest: LingzhiLinkManifest, url: string | undefined): { link: LingzhiLink | null; raw: string } {
  const raw = url?.startsWith(LINGZHI_LINK_PREFIX) ? decodeURIComponent(url.slice(LINGZHI_LINK_PREFIX.length)) : (url ?? '')
  return { link: raw ? findLingzhiLink(manifest, raw) : null, raw }
}
